import React, { useEffect, useState, useTransition } from 'react';

import { GenRouteUrls, Keys, ModuleFieldsData, Modules, ReqUrls } from '../../config/AppConfig';
import { isCurrUserRoleAdmin, printError, printLog, sendRequest } from "../../utils/AppUtils";
import { useTheme } from '../../../common/ProjectThemeProvider';
import { InternalPageHeaderBar, RecordsPagination, ThemedConfirmDialog, ThemedIconButton } from "../../index"
import { Anchor, Flexbox, Label, LoadingSection, Section } from "../../../common/fusion-ui"
import { CssVariant, DefStrNA } from 'common/fusion-ui/config/FusionConfig';
import {
    checkNullArr,
    checkNullStr,
    getArrLen,
    getJsonValue,
    getHighlightedMatchedText,
    isBoolTrue,
    isJsonValueTrue,
    limitStringWords,
    shuffleList,
    sortJsonListAdvance
} from 'common/fusion-ui/utils/ReactUtils';
import { useToast } from "../../../common/fusion-ui/provider/ToastProvider";
import { useConfig } from "../../context/ConfigProvider";
import useScreenSize from "../../../common/fusion-ui/hook/useScreenSize";
import { AnimationType, FramerAnimator } from "../../../common/fusion-ui/animation/FramerAnimator";


const LinkPanel = React.memo(({
    recordList = [],
    loadingStatus = {},
    onRecordsUpdate,
    showControls = true,
    showHeader = true,
    showPagination = true,
}) => {
    const TAG = 'LinkPanel';

    const moduleName = Modules.UsefulLinks;
    const mf = ModuleFieldsData[moduleName];

    const animator = new FramerAnimator();

    const theme = useTheme();
    const { showToast, changeToastVariant } = useToast();
    const { isScreenUpSm } = useScreenSize();
    const { updateConfig } = useConfig();

    const SEARCH_BY_PARAMS = {
        anything: {
            name: 'Anything'
        },
        title: {
            name: mf.title.title
        },
        desc: {
            name: mf.desc.title
        },
        url: {
            name: mf.url.titleAlt
        },
    };
    const SORT_BY_PARAMS = {
        title: {
            name: mf.title.title,
            sortKey: mf.title.column,
        },
        desc: {
            name: mf.desc.title,
            sortKey: mf.desc.column,
        },
        url: {
            name: mf.url.titleAlt,
            sortKey: mf.url.column,
        },
    };
    const PAGINATION_RECORD_LIMITS = [3, 5, 10, 15, 20];
    const DEF_PGNTN_RECORD_LIMIT = PAGINATION_RECORD_LIMITS[0];
    const DEF_PGNTN_START_INDEX = 1;
    const DESC_WORD_LIMIT = isScreenUpSm ? 20 : 40;

    // use transition hook
    const [sortListPending, startSortListTransition] = useTransition();
    const [searchListPending, startSearchListTransition] = useTransition();


    const Fields = {
        recordList: `${TAG}_recordList`,

        isDataLoading: 'isDataLoading',
        isDataFound: 'isDataFound',
        dataLoadingMsg: 'dataLoadingMsg',

        searchInputValue: 'searchInputValue',
        searchBySelectedOption: 'searchBySelectedOption',

        sortBySelectedOption: 'sortBySelectedOption',
        isSortByOrderAsc: 'isSortByOrderAsc',

        pagination_currPageNum: 'pagination_currPageNum',
        pagination_currPageLimit: 'pagination_currPageLimit',

        recordDeleteBtn_Disable: 'recordDeleteBtn_Disable',
        recordDeleteBtn_AsLoading: 'recordDeleteBtn_AsLoading',
        currRecordDeleteId: 'currRecordDeleteId',
        showDeleteDialog: 'showDeleteDialog',
    };
    const [fieldsData, setFieldsData] = useState({});

    useEffect(() => {
        //Initial def-values
        if (!checkNullStr(getField(Fields.searchBySelectedOption))) {
            updateConfig(Keys.tracker_currPageModule, moduleName);
            updateConfig(Keys.tracker_currPageTag, TAG);
            updateField(Fields.searchBySelectedOption, SEARCH_BY_PARAMS.anything.name);
            updateField(Fields.sortBySelectedOption, SORT_BY_PARAMS.title.name);
            updateField(Fields.isSortByOrderAsc, true);
            updateField(Fields.pagination_currPageNum, DEF_PGNTN_START_INDEX);
            updateField(Fields.pagination_currPageLimit, DEF_PGNTN_RECORD_LIMIT);
        }
    }, []);

    useEffect(() => {
        // log('useEffect.recordList:', recordList, 'loadingStatus:', loadingStatus);
        updateField(Fields.isDataLoading, true);
        updateField(Fields.recordList, recordList);
        updateField(Fields.isDataLoading, !isJsonValueTrue(loadingStatus, 'loaded'));
        updateField(Fields.isDataFound, isJsonValueTrue(loadingStatus, 'success'));
        updateField(Fields.dataLoadingMsg, getJsonValue(loadingStatus, 'msg'));
    }, [recordList, loadingStatus]);
    // added by Vandit Tewari on (11/11/24) - made this state to manage the delete process 
    const [isDeleting, setIsDeleting] = useState(false);

    return (<>
        {createPanel()}
        {isBoolTrue(showControls) && createDeleteDialog()}
    </>);


    //======================UI Functions start=====================
    function createPanel() {
        return <div className={''}>
            <div className={'position-relative'}>
                {/*header-bar*/}
                {isBoolTrue(showHeader) && <InternalPageHeaderBar
                    moduleName={moduleName}
                    px={0}

                    uploadUrl={GenRouteUrls.link_uploadPage}
                    recomUrl={GenRouteUrls.link_recomPage}

                    showSearch={true}
                    showSort={true}
                    searchByParams={SEARCH_BY_PARAMS}
                    sortByParams={SORT_BY_PARAMS}

                    recordsLen={getArrLen(getRecordList())}
                    filteredRecordsLen={getArrLen(getRecordList(true))}
                    searchValue={getField(Fields.searchInputValue)}
                    searchByDefOption={getField(Fields.searchBySelectedOption)}
                    sortByDefOption={getField(Fields.sortBySelectedOption)}
                    isSorting={sortListPending}
                    isSearching={searchListPending}
                    isSortAsc={isFieldTrue(Fields.isSortByOrderAsc)}

                    onUploadClick={(e) => {
                    }}
                    onRecomClick={(e) => {
                    }}
                    onSearchChange={(e, value) => handleSearchInputChange(value)}
                    onSearchBy={(name, item, index) => {
                        updateField(Fields.searchBySelectedOption, name);
                        handleSearchInputChange(getField(Fields.searchInputValue), name);
                    }}
                    onSort={(name, item, index, sortKey) => {
                        handleSortByChange(name, sortKey);
                    }}
                />}

                {/*body*/}
                {<div className={'position-relative'}>
                    {/*record-list */}
                    {(isFieldTrue(Fields.isDataFound) && isFieldTrue(Fields.isDataFound)) &&
                        animator.setAnimationType(AnimationType.SLIDE_UP).renderChildren(
                            <div className={""}>
                                {getRecordListToRender().map((item, index) => {
                                    const recordId = getJsonValue(item, mf.recordId.column)
                                    return <div key={index + getJsonValue(item, mf.title.column)}
                                        className={'py-1 mb-3'}>
                                        <Section
                                            className={`h-100 app-section hover`}
                                            borderless={false}
                                            roundedVariant={5}
                                            position={'relative'}
                                            borderColor={theme.text20}
                                            backgroundColor={theme.bg}
                                            contentClassName={'px-1'}>
                                            {/*title-link*/}
                                            <div className={'w-auto d-flex'}>
                                                <Anchor
                                                    wrapperClassName={'w-auto'}
                                                    className={'fw-semi-bold m-0 p-0 text-wrap'}
                                                    href={`${GenRouteUrls.link_internalPage}${recordId}`}>
                                                    <Label className={'m-0 p-0 w-100'} textAt={'start'}>
                                                        {highlightSearchMatchedText((getJsonValue(item, mf.title.column, DefStrNA)), SEARCH_BY_PARAMS.title.name)}
                                                    </Label>
                                                </Anchor>
                                            </div>

                                            {/*desc*/}
                                            <div>
                                                <Label
                                                    className={'m-0 p-0 text-wrap mt-n1 text-block'}
                                                    color={theme.text}
                                                    textAt={'start'}>
                                                    {/* <span className={'fw-semi-bold'}>{mf.desc.title}: </span> */}
                                                    <span>{highlightSearchMatchedText(limitStringWords(getJsonValue(item, mf.desc.column, DefStrNA), 50, ' ', '...'), SEARCH_BY_PARAMS.desc.name)}</span>
                                                </Label>
                                            </div>


                                            {/*link & right-controls*/}
                                            <div className={'mt-n1 w-100 d-flex jc-between jc-start al-center flex-wrap'}>
                                                {/*left - link*/}
                                                <Flexbox className={'m-0 p-0 w-100 mt-1x d-flex flex-nowrap fd-row'}
                                                    justifyAt={'start'} alignAt={'start'}
                                                >
                                                    <span className={'m-0 p-0 fw-semi-bold'}>{mf.url.titleAlt}:&nbsp;</span>

                                                    {checkNullStr(getJsonValue(item, mf.url.column))
                                                        ? <div className={'w-auto d-flex'}>
                                                            <Anchor className={'m-0 text-wrap'} wrapperClassName={'m-0 p-0 w-auto'}
                                                                target={'_blank'} simple={true}
                                                                href={getJsonValue(item, mf.url.column)}>
                                                                {/*{getJsonValue(item, mf.url.column)}*/}
                                                                {highlightSearchMatchedText(getJsonValue(item, mf.url.column), SEARCH_BY_PARAMS.url.name)}
                                                            </Anchor>
                                                        </div>
                                                        : DefStrNA}
                                                </Flexbox>


                                                {/*right - controls*/}
                                                <Flexbox className={''} justifyAt={'start'} alignAt={'center'}>
                                                    {/* edit-btn */}
                                                    {(isCurrUserRoleAdmin() && isBoolTrue(showControls)) && <ThemedIconButton
                                                        className={''}
                                                        tooltip={'Edit'}
                                                        mx={2}
                                                        color={theme.primaryBlue}
                                                        bgColor={theme.textWhite}
                                                        hoverBgColor={theme.primaryBlue}
                                                        borderColor={theme.primaryBlue}
                                                        outlined={true}
                                                        icon={'bi bi bi-pencil'}
                                                        iconSize={'11px'}
                                                        size={'26px'}
                                                        href={`${GenRouteUrls.link_editPage}${recordId}`}
                                                    />}

                                                    {/* delete-btn */}
                                                    {(isCurrUserRoleAdmin() && isBoolTrue(showControls)) && <ThemedIconButton
                                                        className={''}
                                                        tooltip={'Delete'}
                                                        m={1}
                                                        ml={2}
                                                        variant={'red'}
                                                        bgColor={theme.textWhite}
                                                        hoverBgColor={'red'}
                                                        outlined={true}
                                                        icon={'bi bi bi-trash'}
                                                        iconSize={'11px'}
                                                        size={'26px'}
                                                        loadingVariant={CssVariant.danger}
                                                        asLoading={isFieldTrue(Fields.recordDeleteBtn_AsLoading) && getField(Fields.currRecordDeleteId) == recordId}
                                                        disabled={isFieldTrue(Fields.recordDeleteBtn_Disable) && getField(Fields.currRecordDeleteId) == recordId}
                                                        onClick={() => {
                                                            updateField(Fields.currRecordDeleteId, recordId);
                                                            updateField(Fields.showDeleteDialog, true);
                                                        }}
                                                    />}
                                                </Flexbox>
                                            </div>
                                        </Section>
                                    </div>
                                })}
                            </div>)}

                    {/*No search-found*/}
                    {((isSearchOngoing() && !searchListPending && checkNullArr(getRecordList()) && !checkNullArr(getRecordList(true)))) &&
                        <Flexbox className={'h-80 select-none'} justifyAt={'center'} alignAt={'center'} direction={'column'} wrap={'wrap'}>
                            <Label className={'m-1 w-100 fs-xxl fw-semi-bold'} color={theme.primary50}>
                                Search not found!
                            </Label>
                        </Flexbox>}

                    {<LoadingSection
                        labelClassName={'m-1 w-100 fs-xxl fw-semi-bold'}
                        heightClass={75}
                        color={theme.primary50}
                        success={isFieldTrue(Fields.isDataFound)}
                        loading={isFieldTrue(Fields.isDataLoading)}
                        msg={getField(Fields.dataLoadingMsg)}
                    />}
                </div>}

                {/* pagination */}
                {isBoolTrue(showPagination) && <RecordsPagination
                    id={TAG}
                    position={'sticky'}
                    bottom={'-.1rem'}
                    zIndex={1}
                    bgColor={theme.primary10Opaque}
                    scrollToTopOnPageChange={false}
                    totalRecords={getArrLen(getRecordList(true))}
                    totalRecordsToRender={getArrLen(getRecordListToRender())}
                    recordLimits={PAGINATION_RECORD_LIMITS}
                    onPageChange={(e, newPageIndex) => {
                        // log('RecordsPagination.onLimitChange.newPageIndex:', newPageIndex);
                        updateField(Fields.pagination_currPageNum, newPageIndex);
                    }}
                    onLimitChange={(newLimit, newPageIndex) => {
                        // log('RecordsPagination.onLimitChange.newLimit:', newLimit, 'newPageIndex:', newPageIndex);
                        updateField(Fields.pagination_currPageLimit, newLimit);
                        updateField(Fields.pagination_currPageNum, newPageIndex);
                    }}
                />}
            </div>

        </div>
    }

    function createDeleteDialog() {
        return <ThemedConfirmDialog
            // updated by Vandit Tewari on (11/11/24) - added one more condition in open if isDeleting false means no delete operation processed then only open this delete model
            open={isFieldTrue(Fields.showDeleteDialog) && !isDeleting}
            onCancel={() => updateField(Fields.showDeleteDialog, false)}
            onHide={() => updateField(Fields.currRecordDeleteId, null)}
            title={`Delete Useful Link`}
            subMsg={getJsonValue(getRecordList().find(item => getField(Fields.currRecordDeleteId) == getJsonValue(item, mf.recordId.column)), mf.title.column)}
            operation={'delete'}
            target={'this useful-link'}
            onConfirm={() => {
                handleRecordDelete();
            }}
        />
    }

    //======================UI Functions end=====================

    function handleRecordDelete() {
        const fun = 'handleRecordDelete';
        const recordId = getField(Fields.currRecordDeleteId);
        if (checkNullStr(recordId)) {
            updateField(Fields.recordDeleteBtn_Disable, true);
            updateField(Fields.recordDeleteBtn_AsLoading, true);
            // added by Vandit Tewari on (11/11/24) - set it true when delete process start to not conflict with other delete operation when delete ongoing not start
            setIsDeleting(true);  
            try {
                const REQUEST_OPTIONS = { recordId: recordId };
                sendRequest({
                    reqUrl: ReqUrls.link__mainPage_delete_record,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);
                        updateField(Fields.showDeleteDialog, false)

                        const success = isJsonValueTrue(fetchedData, 'success');
                        const message = getJsonValue(fetchedData, 'message');
                        if (success) {
                            toast(message, CssVariant.success);
                            // log(fun, 'getRecordList-old.before:', getArrLen(getRecordList()));


                            //Remove this recordId item from recordList
                            const recordList = getRecordList().filter((item) => recordId != getJsonValue(item, mf.recordId.column));
                            updateField(Fields.recordList, recordList);
                            if (onRecordsUpdate) onRecordsUpdate(recordList);
                            // log(fun, 'getRecordList-after.length:', getArrLen(recordList));

                            if (!checkNullArr(recordList)) {
                                updateField(Fields.isDataFound, false);
                                updateField(Fields.dataLoadingMsg, "No records found!");
                            }
                        } else {
                            toast(message, CssVariant.danger);
                        }
                        // added by Vandit Tewari on (11/11/24) - set it false when operation completed so all the other operations of delete can be processed
                        setIsDeleting(false);  // Operation complete, set isDeleting to false
                        updateField(Fields.currRecordDeleteId, null);
                        updateField(Fields.recordDeleteBtn_Disable, false);
                        updateField(Fields.recordDeleteBtn_AsLoading, false);
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                        // added by Vandit Tewari on (11/11/24) - set it false if operation fails due to any of the follow reason in that case also have to close it
                        setIsDeleting(false);  // Operation complete with error, set isDeleting to false
                        updateField(Fields.recordDeleteBtn_Disable, false);
                        updateField(Fields.recordDeleteBtn_AsLoading, false);
                    }),
                });
            } catch (error) {
                logErr(fun, error);
                setIsDeleting(false);  // Operation failed, set isDeleting to false
                updateField(Fields.recordDeleteBtn_Disable, false);
                updateField(Fields.recordDeleteBtn_AsLoading, false);
            }
        } else {
            toast('Record-id not found!', CssVariant.danger);
        }
    }

    //======================Util Functions start=====================
    function handleSearchInputChange(inputValue, searchByOption = getField(Fields.searchBySelectedOption)) {
        const fun = 'handleSearchInputChange:';
        try {
            // if (inputValue == InputFocusShortcutKey) {
            //     return;
            // }
            // startSearchListTransition(() => {
            updateField(Fields.searchInputValue, inputValue);

            // Filter search query based on selected search filter
            const inputValueL = (inputValue + '').toLowerCase();
            let recordList = getRecordList();
            if (checkNullStr(inputValue) && checkNullArr(recordList)) {
                recordList = recordList.map((item) => {
                    const title = (getJsonValue(item, mf.title.column) + '').toLowerCase();
                    const desc = (getJsonValue(item, mf.desc.column) + '').toLowerCase();
                    const url = (getJsonValue(item, mf.url.column) + '').toLowerCase();

                    let matched = false;
                    switch (searchByOption) {
                        case SEARCH_BY_PARAMS.anything.name:
                            matched = (title + desc + url).includes(inputValueL);
                            break;
                        case SEARCH_BY_PARAMS.title.name:
                            matched = title.includes(inputValueL);
                            break;
                        case SEARCH_BY_PARAMS.desc.name:
                            matched = desc.includes(inputValueL);
                            break;
                        case SEARCH_BY_PARAMS.url.name:
                            matched = url.includes(inputValueL);
                            break;
                    }
                    return {
                        ...item,
                        searched: matched
                    }
                });
                // log(fun, 'inputValue:', inputValue, 'searchBy:', getField(Fields.searchBySelectedOption), 'recordList', recordList);
            }

            // update-list
            startSearchListTransition(() => {
                updateField(Fields.recordList, recordList);
            });
        } catch (error) {
            logErr(fun, error);
            toast('Search failed, please try again', CssVariant.danger);
        }
    }

    function handleSortByChange(name, sortKey) {
        const fun = 'handleSortByChange:';
        try {
            startSortListTransition(() => {
                const sortOption = getField(Fields.sortBySelectedOption);
                const order = name == sortOption ? !isFieldTrue(Fields.isSortByOrderAsc) : isFieldTrue(Fields.isSortByOrderAsc);

                updateField(Fields.isSortByOrderAsc, order);
                updateField(Fields.sortBySelectedOption, name);
                updateField(Fields.recordList, sortJsonListAdvance(shuffleList(getRecordList()), sortKey, order));
            });
        } catch (error) {
            toast('Sort failed, please try again', CssVariant.danger);
            logErr(fun, error);
        }
    }

    function highlightSearchMatchedText(text, searchByParam) {
        const can = (getField(Fields.searchBySelectedOption) == SEARCH_BY_PARAMS.anything.name || getField(Fields.searchBySelectedOption) == searchByParam);
        return can ? getHighlightedMatchedText(text, getField(Fields.searchInputValue)) : text;
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({ ...prevState, [field]: value }));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function getRecordList(searchedOnly = false) {
        if (checkNullArr(getField(Fields.recordList))) {
            if (isBoolTrue(searchedOnly) && isSearchOngoing()) {
                return getField(Fields.recordList).filter(item => isJsonValueTrue(item, 'searched'))
            }
            return getField(Fields.recordList);
        }
        return [];
        // let list = checkNullArr(getField(Fields.recordList)) ? getField(Fields.recordList) : [];
        // if (isBoolTrue(searchedOnly) && isSearchOngoing()) {
        //     list = list.filter(item => isJsonValueTrue(item, 'searched'));
        // }
        // return list;
    }

    function getRecordListToRender() {
        return getRecordList(true).slice((getField(Fields.pagination_currPageNum) - 1) * getField(Fields.pagination_currPageLimit), getField(Fields.pagination_currPageNum) * getField(Fields.pagination_currPageLimit));
    }

    function isSearchOngoing() {
        return checkNullStr(getField(Fields.searchInputValue));
    }


    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
        log(...[text]);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }

    //======================Util Functions end=====================
});

export default LinkPanel;
